import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Modal } from '@repo/design-system/components/ui/modal';
import { toast } from '@repo/design-system/components/ui/toaster';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useBulkMoveMapPinsMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectIsAddingMapLocation, selectIsBulkMovingLocations, selectIsInCreatorMode, selectIsMovingLocation, selectMapData, selectSelectedMarkerId, setIsBulkMovingLocations, updateMapLocationFeatures } from '@repo/store/maps/slices/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { findClosestPoint } from '@repo/types/maps/map-utils';
import * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PIN_INTERACTION_LAYER_IDS } from '../../game-map/hooks/constants';
import { useMapCursor } from '../../game-map/hooks/useMapCursor';
import { useMapContext } from '../../game-map/map-context';
type Coordinates = [number, number];
type MercatorDelta = [number, number];
const MIN_MERCATOR_DELTA = 0.000000000001;
interface DragState {
  anchorPinExternalId: string;
  anchorOriginalCoordinates: Coordinates;
  startDelta: MercatorDelta;
  startMercatorCoordinates: MercatorDelta;
}
interface PreviewState {
  anchorPinExternalId: string | null;
  geometry: {
    type: 'Point';
    coordinates: Coordinates;
  } | null;
  hasMoved: boolean;
}
const INITIAL_PREVIEW_STATE: PreviewState = {
  anchorPinExternalId: null,
  geometry: null,
  hasMoved: false
};
const getIsCustomPin = (feature: {
  properties?: {
    isCustomPin?: unknown;
  };
}) => feature.properties?.isCustomPin === true || feature.properties?.isCustomPin === 'true';
const getFeatureExternalId = (feature: {
  properties?: {
    externalId?: unknown;
  };
}) => typeof feature.properties?.externalId === 'string' ? feature.properties.externalId : null;
const getMercatorCoordinates = ([lng, lat]: Coordinates): MercatorDelta => {
  const coordinates = MapLibre.MercatorCoordinate.fromLngLat({
    lng,
    lat
  });
  return [coordinates.x, coordinates.y];
};
const translateCoordinates = ([lng, lat]: Coordinates, [xDelta, yDelta]: MercatorDelta): Coordinates => {
  const coordinates = MapLibre.MercatorCoordinate.fromLngLat({
    lng,
    lat
  });
  const translated = new MapLibre.MercatorCoordinate(coordinates.x + xDelta, coordinates.y + yDelta).toLngLat();
  return [translated.lng, translated.lat];
};
const hasMeaningfulDelta = ([xDelta, yDelta]: MercatorDelta) => Math.abs(xDelta) > MIN_MERCATOR_DELTA || Math.abs(yDelta) > MIN_MERCATOR_DELTA;
export const BulkMoveMapLocationsAction = () => {
  const $ = _c(41);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["game_map"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useT(t0);
  const dispatch = useAppDispatch();
  const mapData = useAppSelector(selectMapData);
  const isAddingMapLocation = useAppSelector(selectIsAddingMapLocation);
  const isBulkMovingLocations = useAppSelector(selectIsBulkMovingLocations);
  const isMovingLocation = useAppSelector(selectIsMovingLocation);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  let t1;
  if ($[1] !== mapData?.geojson.features) {
    t1 = mapData?.geojson.features.filter(_temp).length ?? 0;
    $[1] = mapData?.geojson.features;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const officialPinCount = t1;
  let t2;
  if ($[3] !== dispatch || $[4] !== t) {
    t2 = () => {
      dispatch(setIsBulkMovingLocations(true));
      setIsConfirmOpen(false);
      toast({
        title: t(_temp2),
        description: t(_temp3),
        variant: "info"
      });
    };
    $[3] = dispatch;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleConfirm = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsConfirmOpen(true);
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = isBulkMovingLocations || isAddingMapLocation || isMovingLocation || officialPinCount === 0;
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = css({
      flex: "1",
      whiteSpace: "nowrap"
    });
    t6 = <Icon icon="open_with" decorative={true} />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t) {
    t7 = t(_temp4);
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t7) {
    t8 = <Button onClick={t3} color="warning" size="sm" disabled={t4} className={t5}>{t6}{t7}</Button>;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = details => setIsConfirmOpen(details.open);
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t(_temp5);
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t) {
    t11 = t(_temp6);
    $[17] = t;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsConfirmOpen(false);
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t) {
    t13 = t(_temp7);
    $[20] = t;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = <Button variant="ghost" onClick={t12} color="secondary" size="sm">{t13}</Button>;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Icon icon="open_with" decorative={true} />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t) {
    t16 = t(_temp8);
    $[25] = t;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== handleConfirm || $[28] !== t16) {
    t17 = <Button onClick={handleConfirm} color="warning" size="sm">{t15}{t16}</Button>;
    $[27] = handleConfirm;
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t14 || $[31] !== t17) {
    t18 = <FlexRow gap="4" justify="end">{t14}{t17}</FlexRow>;
    $[30] = t14;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== isConfirmOpen || $[34] !== t10 || $[35] !== t11 || $[36] !== t18) {
    t19 = <Modal isOpen={isConfirmOpen} onOpenChange={t9} title={t10} description={t11} size="sm">{t18}</Modal>;
    $[33] = isConfirmOpen;
    $[34] = t10;
    $[35] = t11;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t19 || $[39] !== t8) {
    t20 = <>{t8}{t19}</>;
    $[38] = t19;
    $[39] = t8;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
};
export const BulkMoveMapLocationsControls = () => {
  const $ = _c(81);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["game_map"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useT(t0);
  const dispatch = useAppDispatch();
  const {
    map
  } = useMapContext();
  const {
    setCursor
  } = useMapCursor();
  const externalMapId = useAppSelector(selectExternalMapId);
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const isBulkMovingLocations = useAppSelector(selectIsBulkMovingLocations);
  const mapData = useAppSelector(selectMapData);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const [bulkMoveMapPins, t1] = useBulkMoveMapPinsMutation();
  const {
    isLoading
  } = t1;
  const [preview, setPreview] = useState(INITIAL_PREVIEW_STATE);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const baseFeaturesRef = useRef(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 0];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const currentDeltaRef = useRef(t3);
  const dragStateRef = useRef(null);
  const dragPanWasEnabledRef = useRef(null);
  const selectedMarkerIdRef = useRef(selectedMarkerId);
  const mapDataRef = useRef(mapData);
  const isLoadingRef = useRef(isLoading);
  let t4;
  let t5;
  if ($[3] !== isLoading) {
    t4 = () => {
      isLoadingRef.current = isLoading;
    };
    t5 = [isLoading];
    $[3] = isLoading;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== selectedMarkerId) {
    t6 = () => {
      selectedMarkerIdRef.current = selectedMarkerId;
    };
    t7 = [selectedMarkerId];
    $[6] = selectedMarkerId;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[9] !== mapData) {
    t8 = () => {
      mapDataRef.current = mapData;
    };
    t9 = [mapData];
    $[9] = mapData;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  useEffect(t8, t9);
  let t10;
  if ($[12] !== map) {
    t10 = features => {
      if (!map) {
        return;
      }
      const source = map.getSource("markers") as MapLibre.GeoJSONSource | undefined;
      source?.setData({
        type: "FeatureCollection",
        features
      } as GeoJSON.FeatureCollection);
      const selectedSource = map.getSource("selected-marker") as MapLibre.GeoJSONSource | undefined;
      const markerId = selectedMarkerIdRef.current;
      if (!selectedSource) {
        return;
      }
      if (!markerId) {
        selectedSource.setData({
          type: "FeatureCollection",
          features: []
        } as GeoJSON.FeatureCollection);
        return;
      }
      const selectedFeature = features.find(feature => feature.properties.externalId === markerId);
      selectedSource.setData({
        type: "FeatureCollection",
        features: selectedFeature ? [{
          ...selectedFeature,
          id: selectedFeature.properties.externalId,
          properties: {
            ...selectedFeature.properties,
            selected: true
          }
        }] : []
      } as GeoJSON.FeatureCollection);
    };
    $[12] = map;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const setMarkerSourceFeatures = t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = delta => baseFeaturesRef.current.map(feature_0 => {
      if (feature_0.properties.isCustomPin) {
        return feature_0;
      }
      return {
        ...feature_0,
        geometry: {
          ...feature_0.geometry,
          coordinates: translateCoordinates(feature_0.geometry.coordinates, delta)
        }
      };
    });
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const getMovedFeatures = t11;
  let t12;
  if ($[15] !== setMarkerSourceFeatures) {
    t12 = delta_0 => {
      setMarkerSourceFeatures(getMovedFeatures(delta_0));
    };
    $[15] = setMarkerSourceFeatures;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const applyPreview = t12;
  let t13;
  if ($[17] !== setMarkerSourceFeatures) {
    t13 = () => {
      if (baseFeaturesRef.current.length > 0) {
        setMarkerSourceFeatures(baseFeaturesRef.current);
      }
    };
    $[17] = setMarkerSourceFeatures;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const restorePreview = t13;
  let t14;
  if ($[19] !== dispatch || $[20] !== restorePreview || $[21] !== setCursor) {
    t14 = () => {
      if (isLoadingRef.current) {
        return;
      }
      restorePreview();
      currentDeltaRef.current = [0, 0];
      dragStateRef.current = null;
      setPreview(INITIAL_PREVIEW_STATE);
      dispatch(setIsBulkMovingLocations(false));
      setCursor("grab");
    };
    $[19] = dispatch;
    $[20] = restorePreview;
    $[21] = setCursor;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const handleCancel = t14;
  let t15;
  if ($[23] !== bulkMoveMapPins || $[24] !== dispatch || $[25] !== externalMapId || $[26] !== preview || $[27] !== t) {
    t15 = async () => {
      if (!externalMapId || !preview.anchorPinExternalId || !preview.geometry || !preview.hasMoved) {
        toast({
          title: t(_temp9),
          variant: "error"
        });
        return;
      }
      ;
      try {
        const result = await bulkMoveMapPins({
          gameMapExternalId: externalMapId,
          anchorPinExternalId: preview.anchorPinExternalId,
          geometry: preview.geometry
        }).unwrap();
        dispatch(updateMapLocationFeatures([...result.features]));
        dispatch(setIsBulkMovingLocations(false));
        setPreview(INITIAL_PREVIEW_STATE);
        baseFeaturesRef.current = [];
        currentDeltaRef.current = [0, 0];
        toast({
          title: t(_temp10),
          variant: "success"
        });
      } catch (t16) {
        const error = t16;
        toast({
          title: t(_temp11),
          description: error instanceof Error ? error.message : JSON.stringify(error),
          variant: "error"
        });
      }
    };
    $[23] = bulkMoveMapPins;
    $[24] = dispatch;
    $[25] = externalMapId;
    $[26] = preview;
    $[27] = t;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const handleConfirm = t15;
  let t16;
  let t17;
  if ($[29] !== applyPreview || $[30] !== handleCancel || $[31] !== isBulkMovingLocations || $[32] !== map || $[33] !== setCursor) {
    t16 = () => {
      if (!isBulkMovingLocations || !map || !mapDataRef.current) {
        return;
      }
      const targetMap = map;
      const markerLayers = PIN_INTERACTION_LAYER_IDS.filter(layer => targetMap.getLayer(layer));
      if (markerLayers.length === 0) {
        return;
      }
      baseFeaturesRef.current = [...mapDataRef.current.geojson.features];
      currentDeltaRef.current = [0, 0];
      dragStateRef.current = null;
      setPreview(INITIAL_PREVIEW_STATE);
      setCursor("grab");
      const handleMouseDown = e => {
        if (isLoadingRef.current) {
          return;
        }
        const officialPoints = e.features?.filter(_temp12) ?? [];
        const closestPoint = findClosestPoint(officialPoints, e.lngLat);
        const anchorPinExternalId = closestPoint ? getFeatureExternalId(closestPoint) : null;
        if (!anchorPinExternalId) {
          return;
        }
        const anchorFeature = baseFeaturesRef.current.find(feature_2 => feature_2.properties.externalId === anchorPinExternalId);
        if (!anchorFeature) {
          return;
        }
        e.preventDefault();
        dragStateRef.current = {
          anchorPinExternalId,
          anchorOriginalCoordinates: anchorFeature.geometry.coordinates,
          startDelta: currentDeltaRef.current,
          startMercatorCoordinates: getMercatorCoordinates([e.lngLat.lng, e.lngLat.lat])
        };
        dragPanWasEnabledRef.current = targetMap.dragPan.isEnabled();
        targetMap.dragPan.disable();
        setCursor("grabbing");
      };
      const handleMouseMove = e_0 => {
        const dragState = dragStateRef.current;
        if (!dragState) {
          return;
        }
        const nextMercatorCoordinates = getMercatorCoordinates([e_0.lngLat.lng, e_0.lngLat.lat]);
        const nextDelta = [dragState.startDelta[0] + nextMercatorCoordinates[0] - dragState.startMercatorCoordinates[0], dragState.startDelta[1] + nextMercatorCoordinates[1] - dragState.startMercatorCoordinates[1]];
        currentDeltaRef.current = nextDelta;
        applyPreview(nextDelta);
      };
      const finishDrag = () => {
        const dragState_0 = dragStateRef.current;
        if (!dragState_0) {
          return;
        }
        const delta_1 = currentDeltaRef.current;
        const coordinates = translateCoordinates(dragState_0.anchorOriginalCoordinates, delta_1);
        setPreview({
          anchorPinExternalId: dragState_0.anchorPinExternalId,
          geometry: {
            type: "Point",
            coordinates
          },
          hasMoved: hasMeaningfulDelta(delta_1)
        });
        dragStateRef.current = null;
        if (dragPanWasEnabledRef.current) {
          targetMap.dragPan.enable();
        }
        dragPanWasEnabledRef.current = null;
        setCursor("grab");
      };
      const handleKeyDown = e_1 => {
        if (e_1.key === "Escape") {
          e_1.preventDefault();
          handleCancel();
        }
      };
      targetMap.on("mousedown", markerLayers, handleMouseDown);
      targetMap.on("mousemove", handleMouseMove);
      targetMap.on("mouseup", finishDrag);
      document.addEventListener("mouseup", finishDrag);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        targetMap.off("mousedown", markerLayers, handleMouseDown);
        targetMap.off("mousemove", handleMouseMove);
        targetMap.off("mouseup", finishDrag);
        document.removeEventListener("mouseup", finishDrag);
        document.removeEventListener("keydown", handleKeyDown);
        if (dragPanWasEnabledRef.current) {
          targetMap.dragPan.enable();
        }
        dragPanWasEnabledRef.current = null;
        setCursor("grab");
      };
    };
    t17 = [applyPreview, handleCancel, isBulkMovingLocations, map, setCursor];
    $[29] = applyPreview;
    $[30] = handleCancel;
    $[31] = isBulkMovingLocations;
    $[32] = map;
    $[33] = setCursor;
    $[34] = t16;
    $[35] = t17;
  } else {
    t16 = $[34];
    t17 = $[35];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[36] !== handleCancel || $[37] !== isBulkMovingLocations || $[38] !== isInCreatorMode || $[39] !== isLoading) {
    t18 = () => {
      if (!isInCreatorMode && isBulkMovingLocations && !isLoading) {
        handleCancel();
      }
    };
    t19 = [handleCancel, isBulkMovingLocations, isInCreatorMode, isLoading];
    $[36] = handleCancel;
    $[37] = isBulkMovingLocations;
    $[38] = isInCreatorMode;
    $[39] = isLoading;
    $[40] = t18;
    $[41] = t19;
  } else {
    t18 = $[40];
    t19 = $[41];
  }
  useEffect(t18, t19);
  if (!isInCreatorMode || !isBulkMovingLocations) {
    return null;
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = css({
      position: "absolute",
      top: "16",
      left: "[50%]",
      transform: "translateX(-50%)",
      zIndex: 3,
      width: "[min(420px, calc(100vw - 32px))]",
      backgroundColor: "blackAlpha.80",
      border: "[1px solid]",
      borderColor: "whiteAlpha.10",
      borderRadius: "8",
      boxShadow: "xl",
      padding: "12",
      pointerEvents: "auto"
    });
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t) {
    t21 = t(_temp13);
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t21) {
    t22 = <Type variant="header-xxs" color="white">{t21}</Type>;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== preview.hasMoved || $[48] !== t) {
    t23 = preview.hasMoved && <Type variant="body-xs" color="text.secondary">{t(_temp14)}</Type>;
    $[47] = preview.hasMoved;
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <FlexRow gap="8" align="center" justify="between">{t22}{t23}</FlexRow>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t) {
    t25 = t(_temp15);
    $[53] = t;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t25) {
    t26 = <Type variant="body-xs" color="text.secondary">{t25}</Type>;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t) {
    t27 = t(_temp16);
    $[57] = t;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== handleCancel || $[60] !== isLoading || $[61] !== t27) {
    t28 = <Button variant="ghost" onClick={handleCancel} color="secondary" size="sm" disabled={isLoading}>{t27}</Button>;
    $[59] = handleCancel;
    $[60] = isLoading;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  const t29 = isLoading || !preview.hasMoved;
  let t30;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Icon icon="check" decorative={true} />;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== isLoading || $[65] !== t) {
    t31 = isLoading ? t(_temp17) : t(_temp18);
    $[64] = isLoading;
    $[65] = t;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== handleConfirm || $[68] !== t29 || $[69] !== t31) {
    t32 = <Button onClick={handleConfirm} color="success" size="sm" disabled={t29}>{t30}{t31}</Button>;
    $[67] = handleConfirm;
    $[68] = t29;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== t28 || $[72] !== t32) {
    t33 = <FlexRow gap="8" justify="end">{t28}{t32}</FlexRow>;
    $[71] = t28;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t24 || $[75] !== t26 || $[76] !== t33) {
    t34 = <FlexColumn gap="10">{t24}{t26}{t33}</FlexColumn>;
    $[74] = t24;
    $[75] = t26;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t20 || $[79] !== t34) {
    t35 = <div className={t20}>{t34}</div>;
    $[78] = t20;
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  return t35;
};
function _temp(feature) {
  return !feature.properties.isCustomPin;
}
function _temp2($) {
  return $.bulk_move_mode_active;
}
function _temp3($_0) {
  return $_0.bulk_move_drag_an_official_pin;
}
function _temp4($_1) {
  return $_1.move_all_official_pins;
}
function _temp5($_2) {
  return $_2.move_all_official_pins;
}
function _temp6($_3) {
  return $_3.bulk_move_official_pins_confirmation;
}
function _temp7($_4) {
  return $_4.cancel;
}
function _temp8($_5) {
  return $_5.enter_bulk_move_mode;
}
function _temp9($) {
  return $.bulk_move_select_anchor_first;
}
function _temp10($_1) {
  return $_1.bulk_move_success;
}
function _temp11($_0) {
  return $_0.bulk_move_failed;
}
function _temp12(feature_1) {
  return feature_1.geometry.type === "Point" && !getIsCustomPin(feature_1);
}
function _temp13($_2) {
  return $_2.bulk_move_mode_active;
}
function _temp14($_3) {
  return $_3.bulk_move_preview_ready;
}
function _temp15($_4) {
  return $_4.bulk_move_drag_an_official_pin;
}
function _temp16($_5) {
  return $_5.cancel;
}
function _temp17($_6) {
  return $_6.saving;
}
function _temp18($_7) {
  return $_7.confirm_bulk_move;
}